import { DualListBox } from '@/components/wm';
import { apiRequest } from '@/utils/request';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function SelectAssets ({ data, onChangeField, onCompleted }) {
    const
        [externalAssetOptions, setExternalAssetOptions] = useState([]),
        [isLoadingAssets, setIsLoadingAssets] = useState(false),
        [totalAssetsCount, setTotalAssetsCount] = useState(0);

    const formSchema = z.object({
        assetsAttributes: z.array(z.any()).min(1, 'Please add at least one asset')
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            assetsAttributes: data.assetsAttributes || []
        },
        mode: 'onChange'
    });

    const selectedAssets = form.watch('assetsAttributes');

    const errorMessage = useMemo(() => {
        if (externalAssetOptions.length === 0) {
            return i18next.t('integrations.nextbitt.no_assets_found').capitalize();
        }
        return form.formState.errors.assetsAttributes?.message;
    }, [form.formState.errors.assetsAttributes, externalAssetOptions.length]);

    const fetchExternalAssets = async (term = '') => {
        setIsLoadingAssets(true);
        try {
            const payload = {
                license_id: data.licenseId,
                partner_id: data.partnerId,
                site_id: data.siteId,
                configuration_id: data.configurationId,
                search_text: term
            };

            const response = await apiRequest('/integrations/external_assets', 'POST', payload);

            // Map API response to labels/values for the CheckboxGroup
            const options = (response.data.assets || []).map(asset => ({
                label: asset.name,
                value: String(asset.asset_reference), // Component uses 'key', not 'value'
                raw: {
                    name: asset.name,
                    asset_reference: asset.asset_reference,
                    local_reference: asset.local_reference || 0,
                    create_work_orders: asset.create_work_orders || true,
                    create_markers: asset.create_markers || true
                }
            }));

            setExternalAssetOptions(options);
            setTotalAssetsCount(response.data.total_count || 0);
        } catch (err) {
            WiseMetering.layout.showTipper('error', 'Failed to fetch assets');
        } finally {
            setIsLoadingAssets(false);
        }
    };

    const combinedOptions = useMemo(() => {
        // Create a Map of the current API results
        const optionsMap = new Map(externalAssetOptions.map(o => [o.value, o]));

        // Add currently selected assets to that map so they always have an option object
        selectedAssets.forEach(asset => {
            const val = String(asset.asset_reference);
            if (!optionsMap.has(val)) {
                optionsMap.set(val, {
                    label: asset.name,
                    value: val,
                    raw: asset
                });
            }
        });

        return Array.from(optionsMap.values());
    }, [externalAssetOptions, selectedAssets]);

    const handleAssetsChange = newIds => {
        // DualListBox usually returns an array of IDs.
        // We need to map those IDs back to the 'raw' objects for assetsAttributes.
        const fullAssets = combinedOptions
            .filter(opt => newIds.includes(opt.value))
            .map(opt => opt.raw);

        form.setValue('assetsAttributes', fullAssets, { shouldValidate: true });

        if (onChangeField) {
            onChangeField('assetsAttributes')(fullAssets);
        }
    };

    useEffect(() => {
        fetchExternalAssets();
    }, []);

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid, onCompleted]);

    return (
        <div className="tw-w-full">
            {isLoadingAssets ? (
                <p className="tw-text-[12px] tw-ml-2 tw-text-gray-400 tw-mt-1">{i18next.t('glossary.loading').capitalize()}...</p>
            ) : externalAssetOptions.length > 0 ? (
                <p className="tw-text-[12px] tw-ml-2 tw-text-gray-400 tw-mt-1">
                    {
                        totalAssetsCount > externalAssetOptions.length
                            ? i18next.t('integrations.nextbitt.showing_assets', { options: externalAssetOptions.length, total: totalAssetsCount })
                            : i18next.t('integrations.nextbitt.total_assets', { count: totalAssetsCount })
                    }
                </p>
            ) : (
                <p className="tw-text-[12px] tw-ml-2 tw-text-gray-400 tw-mt-1">{i18next.t('integrations.nextbitt.no_assets_found').capitalize()}</p>
            )}
            <DualListBox
                error={errorMessage}
                isLoading={isLoadingAssets}
                onChange={handleAssetsChange}
                onSearchEnter={term => fetchExternalAssets(term)} // Receive term here
                options={combinedOptions}
                searchPlaceholder={i18next.t('integrations.nextbitt.search_assets')}
                selectedIds={selectedAssets.map(a => String(a.asset_reference))}
                title={i18next.t('integrations.nextbitt.assets')}
            />
        </div>
    );
}
